import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, Checkbox, Cluster, HintMark, Input, Select, Stack, guardSubmit, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import { mailboxApi, type MailboxReplyTemplate, type MailboxSettings } from "../../lib/mailboxApi";
import { useLocale } from "../../shell/LocaleProvider";

const INTERVALS = [
  { value: "5", label: "Every 5 minutes" },
  { value: "15", label: "Every 15 minutes" },
  { value: "30", label: "Every 30 minutes" },
  { value: "60", label: "Every hour" },
  { value: "240", label: "Every 4 hours" },
  { value: "1440", label: "Once a day" },
];

function blankTemplate(): MailboxReplyTemplate {
  return {
    id: crypto.randomUUID(),
    name: "",
    intent: "support",
    subject: "",
    body: "",
  };
}

const OAUTH_ERRORS: Record<string, string> = {
  denied: "Microsoft sign-in was cancelled.",
  expired: "That Microsoft sign-in expired. Start again.",
  not_configured: "Microsoft sign-in is not set up on this server.",
  no_mailbox: "Save the mailbox address, then sign in with Microsoft.",
  consent: "A Microsoft 365 admin has to approve Saleslox before this mailbox can be connected.",
  failed: "Microsoft sign-in could not be completed.",
};

const EMPTY: MailboxSettings = {
  id: null,
  label: "Primary",
  host: "",
  port: 993,
  username: "",
  use_ssl: true,
  folder: "INBOX",
  enabled: false,
  scan_interval_minutes: 15,
  auto_convert: false,
  auto_create_leads: false,
  auto_create_support: false,
  auto_assign: false,
  auto_reply: false,
  reply_templates: [],
  auth_mode: "basic",
  oauth_connected: false,
  oauth_account_email: null,
  microsoft_oauth_available: false,
  password_configured: false,
  last_tested_at: null,
  last_error: null,
  last_scan_at: null,
  scan_error: null,
  ai_ready: false,
};

export function MailboxSettingsCard({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { formatDateTime } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState<MailboxSettings>(EMPTY);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Same login-autofill guard as the AI connection fields.
  const [hostOpen, setHostOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    mailboxApi.settings(orgId).then((row) => {
      if (!cancelled) setValue({ ...EMPTY, ...row.mailbox });
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Mailbox settings could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    const flag = searchParams.get("mailbox");
    if (flag !== "connected" && flag !== "error") return;
    if (flag === "connected") {
      setNotice("Signed in with Microsoft.");
      setError(null);
    } else {
      const reason = searchParams.get("reason") || "failed";
      setError(OAUTH_ERRORS[reason] || OAUTH_ERRORS.failed);
      setNotice(null);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("mailbox");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  function saveBody() {
    return {
      label: value.label,
      host: value.host.trim(),
      port: value.port,
      username: value.username.trim(),
      password: value.auth_mode === "microsoft" ? "" : password,
      use_ssl: value.use_ssl,
      folder: value.folder.trim() || "INBOX",
      enabled: value.enabled,
      scan_interval_minutes: value.scan_interval_minutes,
      auto_convert: value.auto_create_leads || value.auto_create_support,
      auto_create_leads: value.auto_create_leads,
      auto_create_support: value.auto_create_support,
      auto_assign: value.auto_assign,
      auto_reply: value.auto_reply,
      reply_templates: value.reply_templates,
      auth_mode: value.auth_mode,
    };
  }

  function signInWithMicrosoft() {
    const wantEnabled = value.enabled;
    setSaving(true);
    setError(null);
    setNotice(null);
    const body = saveBody();
    if (!value.oauth_connected) body.enabled = false;
    return mailboxApi.save(orgId, body).then((row) => {
      setValue({ ...EMPTY, ...row.mailbox });
      setPassword("");
      return mailboxApi.microsoftStart(orgId, { enable_when_connected: wantEnabled });
    }).then((started) => {
      window.location.assign(started.authorization_url);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : "Microsoft sign-in could not be started.");
      setSaving(false);
    });
  }

  return (
    <Card data-testid="mailbox-settings" data-tour="org-mailbox">
      <form
        autoComplete="off"
        onSubmit={guardSubmit((event) => {
          event.preventDefault();
          setSaving(true);
          setError(null);
          setNotice(null);
          return mailboxApi.save(orgId, saveBody()).then((row) => {
            setValue({ ...EMPTY, ...row.mailbox });
            setPassword("");
            setNotice("Mailbox settings saved.");
            reportSaved(show);
          }).catch((err) => {
            setError(reportSaveFailure(show, err, "Mailbox settings could not be saved."));
          }).finally(() => setSaving(false));
        })}
      >
        <Stack gap={4}>
          <h2 className="home-section-title">Shared mailbox</h2>
          <p className="home-subtitle">
            Optional. Organisation admins and CRM app admins read this mailbox. A matched company becomes a
            lead in that territory, with the original email on the lead, and the person who picks it up replies
            from there. Mail that matches no company stays here. Calendar is separate: each person sees their own
            meetings, a manager also sees meetings for people who report to them, and organisation admins and app
            admins see every meeting.
          </p>
          <Checkbox
            label="Enable the shared mailbox"
            checked={value.enabled}
            onChange={(event) => setValue({ ...value, enabled: event.target.checked })}
          />
          <Select
            label="Sign-in"
            value={value.auth_mode}
            onChange={(event) => {
              const auth_mode = event.target.value === "microsoft" ? "microsoft" : "basic";
              setValue({
                ...value,
                auth_mode,
                host: auth_mode === "microsoft" ? "outlook.office365.com" : value.host,
                port: auth_mode === "microsoft" ? 993 : value.port,
                use_ssl: auth_mode === "microsoft" ? true : value.use_ssl,
              });
            }}
          >
            <option value="basic">Password</option>
            <option value="microsoft">Microsoft 365</option>
          </Select>
          <p className="home-subtitle">
            {value.auth_mode === "microsoft"
              ? "Microsoft 365 does not accept a password for this mailbox. Enter the address to read, then sign in as the person who can open it."
              : "Use a password or app password for hosts that still allow it."}
          </p>
          <Input
            label="IMAP host"
            name="mailbox-host"
            autoComplete="off"
            readOnly={!hostOpen}
            onFocus={() => setHostOpen(true)}
            value={value.host}
            onChange={(event) => setValue({ ...value, host: event.target.value })}
          />
          <Cluster gap={3}>
            <Input
              label="Port"
              inputMode="numeric"
              value={String(value.port)}
              onChange={(event) => setValue({ ...value, port: Number(event.target.value.replace(/\D/g, "")) || 993 })}
            />
            <Input
              label="Folder"
              value={value.folder}
              onChange={(event) => setValue({ ...value, folder: event.target.value })}
            />
          </Cluster>
          <Checkbox
            label="Use SSL (port 993)"
            checked={value.use_ssl}
            onChange={(event) => setValue({ ...value, use_ssl: event.target.checked })}
          />
          <Input
            label={value.auth_mode === "microsoft" ? "Shared mailbox address" : "Username"}
            name="mailbox-username"
            autoComplete="off"
            readOnly={!userOpen}
            onFocus={() => setUserOpen(true)}
            value={value.username}
            placeholder={value.auth_mode === "microsoft" ? "accounts@contoso.com" : undefined}
            onChange={(event) => setValue({ ...value, username: event.target.value })}
          />
          {value.auth_mode === "microsoft" ? (
            <p className="home-subtitle">
              This is the mailbox Saleslox reads. A shared mailbox has no password of its own.
              {value.oauth_connected
                ? ` Signed in as ${value.oauth_account_email || "a Microsoft account"}.`
                : " On the Microsoft page, sign in as the person who has access to it."}
            </p>
          ) : null}
          {value.auth_mode === "basic" ? (
            <Input
              label="Password or app password"
              name="mailbox-password"
              type="password"
              autoComplete="new-password"
              readOnly={!passwordOpen}
              onFocus={() => setPasswordOpen(true)}
              value={password}
              placeholder={value.password_configured ? "Leave blank to keep the saved password" : undefined}
              onChange={(event) => setPassword(event.target.value)}
            />
          ) : null}
          {value.auth_mode === "microsoft" && !value.microsoft_oauth_available ? (
            <p className="home-subtitle">Microsoft sign-in is not set up on this server yet.</p>
          ) : null}
          <Select
            label="Check for new mail"
            value={String(value.scan_interval_minutes)}
            onChange={(event) => setValue({ ...value, scan_interval_minutes: Number(event.target.value) })}
          >
            {INTERVALS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </Select>
          <p className="home-subtitle" data-testid="mailbox-lookback-note">
            Saleslox does not load the whole mailbox. Each check reads mail from the last 60 days only.
            Older messages stay in the mailbox.
          </p>
          <p className="home-subtitle">
            AI can read new mail and create a lead or a support case. Choose that under AI settings.
            {" "}
            <button
              type="button"
              className="mailbox-text-button"
              onClick={() => setSearchParams({ tab: "ai", section: "mailbox" })}
            >
              Open AI settings
            </button>
          </p>
          {value.last_scan_at ? <p className="home-subtitle">Last check: {formatDateTime(value.last_scan_at)}</p> : null}
          {value.scan_error ? <p role="alert" className="settings-error">{value.scan_error}</p> : null}
          {value.last_error ? <p role="alert" className="settings-error">{value.last_error}</p> : null}
          {error ? <p role="alert" className="settings-error">{error}</p> : null}
          {notice ? <p className="home-subtitle">{notice}</p> : null}
          <Cluster gap={3}>
            <Button type="submit" data-testid="mailbox-settings-save" disabled={saving}>
              {saving ? "Saving…" : "Save mailbox settings"}
            </Button>
            {value.auth_mode === "microsoft" ? (
              <Button
                type="button"
                variant="secondary"
                disabled={saving || !value.microsoft_oauth_available}
                onClick={() => {
                  void signInWithMicrosoft();
                }}
              >
                {value.oauth_connected ? "Sign in again" : "Sign in with Microsoft"}
              </Button>
            ) : null}
            {value.auth_mode === "microsoft" && value.oauth_connected ? (
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  setSaving(true);
                  setError(null);
                  setNotice(null);
                  void mailboxApi.microsoftDisconnect(orgId).then((row) => {
                    setValue({ ...EMPTY, ...row.mailbox });
                    setNotice("Microsoft sign-in removed. The mailbox is off until someone signs in again.");
                  }).catch((err) => {
                    setError(err instanceof Error ? err.message : "Microsoft could not be disconnected.");
                  }).finally(() => setSaving(false));
                }}
              >
                Disconnect Microsoft
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              disabled={saving || (value.auth_mode === "microsoft"
                ? !value.oauth_connected
                : !value.password_configured && !password)}
              onClick={() => {
                setSaving(true);
                setError(null);
                const pending = password || value.auth_mode === "microsoft"
                  ? mailboxApi.save(orgId, saveBody()).then((row) => setValue({ ...EMPTY, ...row.mailbox }))
                  : Promise.resolve();
                void pending.then(() => mailboxApi.test(orgId)).then(() => {
                  setPassword("");
                  setNotice("Connection succeeded.");
                }).catch((err) => {
                  setError(err instanceof Error ? err.message : "The mailbox could not be reached.");
                }).finally(() => setSaving(false));
              }}
            >
              Test connection
            </Button>
            <Button
              type="button"
              variant="secondary"
              data-testid="mailbox-scan-now"
              disabled={saving || !value.enabled}
              onClick={() => {
                setSaving(true);
                setError(null);
                void mailboxApi.scan(orgId).then((row) => {
                  setNotice(`Checked the mailbox. ${row.stored} new, ${row.classified} classified.`);
                  return mailboxApi.settings(orgId);
                }).then((row) => setValue({ ...EMPTY, ...row.mailbox })).catch((err) => {
                  setError(err instanceof Error ? err.message : "The mailbox could not be checked.");
                }).finally(() => setSaving(false));
              }}
            >
              Check now
            </Button>
          </Cluster>
        </Stack>
      </form>
    </Card>
  );
}

/** Organisation settings → AI → Mailbox. Which messages become records, and who owns them. */
export function MailboxAiSettings({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [value, setValue] = useState<MailboxSettings>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    mailboxApi.settings(orgId).then((row) => {
      if (!cancelled) setValue({ ...EMPTY, ...row.mailbox });
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Mailbox settings could not be loaded.");
    }).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (!loaded) return <p>Loading mailbox AI settings…</p>;

  const ready = Boolean(value.id);
  const canTurnOn = value.ai_ready && ready;

  function updateTemplate(id: string, patch: Partial<MailboxReplyTemplate>) {
    setValue({
      ...value,
      reply_templates: value.reply_templates.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    });
  }

  function saveBody() {
    return {
      label: value.label,
      host: value.host.trim(),
      port: value.port,
      username: value.username.trim(),
      password: "",
      use_ssl: value.use_ssl,
      folder: value.folder.trim() || "INBOX",
      enabled: value.enabled,
      scan_interval_minutes: value.scan_interval_minutes,
      auto_create_leads: value.auto_create_leads,
      auto_create_support: value.auto_create_support,
      auto_assign: value.auto_assign,
      auto_reply: value.auto_reply,
      reply_templates: value.reply_templates,
      auth_mode: value.auth_mode,
    };
  }

  return (
    <Card data-testid="mailbox-ai-settings">
      <form
        onSubmit={guardSubmit((event) => {
          event.preventDefault();
          if (!ready) return undefined;
          setSaving(true);
          setError(null);
          setNotice(null);
          return mailboxApi.save(orgId, saveBody()).then((row) => {
            setValue({ ...EMPTY, ...row.mailbox });
            setNotice("Mailbox AI settings saved.");
            reportSaved(show);
          }).catch((err) => {
            setError(reportSaveFailure(show, err, "Mailbox AI settings could not be saved."));
          }).finally(() => setSaving(false));
        })}
      >
        <Stack gap={3}>
          <div>
            <h2 className="home-section-title">Mailbox</h2>
            <p className="home-subtitle">
              Enable AI to read the email and automatically create:
            </p>
          </div>
          {!ready ? (
            <p className="home-subtitle">
              Set up the shared mailbox under App Settings first. These options are saved with that mailbox.
            </p>
          ) : null}
          <Checkbox
            label="Support (queries and questions)"
            tooltip="A confident email that asks a question or reports a problem becomes a support case, when the sender is already a contact on a company. Leave this unticked and that email stays in the mailbox."
            checked={value.auto_create_support}
            disabled={!canTurnOn || saving}
            onChange={(event) => setValue({ ...value, auto_create_support: event.target.checked })}
          />
          <Checkbox
            label="Leads"
            tooltip="A confident sales enquiry becomes a lead on the matching company. Leave this unticked and the email is still classified, but no lead is created and no lead reply is sent."
            checked={value.auto_create_leads}
            disabled={!canTurnOn || saving}
            onChange={(event) => setValue({ ...value, auto_create_leads: event.target.checked })}
          />
          <Checkbox
            label="Auto assignment to a sales person"
            tooltip="The new lead or support case is given to a manager, who can reassign it. If no manager is set up, the lead stays in the company territory for someone to pick up."
            checked={value.auto_assign}
            disabled={!canTurnOn || saving}
            onChange={(event) => setValue({ ...value, auto_assign: event.target.checked })}
          />
          <Checkbox
            label="AI auto reply"
            tooltip="After that record is created, Saleslox sends the saved template for leads or for support. The model does not write the email. The mailbox must sign in with a password. A Microsoft sign-in can read mail, but it cannot send this reply."
            checked={value.auto_reply}
            disabled={!canTurnOn || saving}
            onChange={(event) => setValue({ ...value, auto_reply: event.target.checked })}
          />
          <p className="home-subtitle">
            {value.ai_ready
              ? "AI still classifies every new email. Untick Leads and a sales email is not turned into a lead. A ticked option creates that record when the model is confident. Auto assignment gives the new lead or support case to a manager, who can reassign it. If no manager is set up, the lead stays in the territory for someone to pick up. AI auto reply sends the saved template for that kind of email only after the record is created. The model does not write the customer email. A reply is sent from a mailbox that signs in with a password."
              : "Automatic records and replies need AI. An admin can still turn a message into a lead or a support case by hand."}
          </p>
          <div>
            <h3 className="home-section-title">Email templates</h3>
            <p className="home-subtitle">
              One template for leads and one for support is enough. Use {"{{contact_name}}"}, {"{{company_name}}"}, {"{{subject}}"}, and {"{{summary}}"} where the customer's details should appear.
            </p>
          </div>
          {value.reply_templates.map((template) => (
            <Stack key={template.id} gap={2}>
              <Input
                label="Template name"
                tooltip="A name you will recognise, such as New enquiry or We received your question. The customer does not see this name."
                value={template.name}
                disabled={!ready || saving}
                onChange={(event) => updateTemplate(template.id, { name: event.target.value })}
              />
              <Select
                label="Use this template for"
                tooltip="A lead template is sent only after a lead is created. A support template is sent only after a support case is created. The first saved template for that kind of email is the one that goes out."
                value={template.intent}
                disabled={!ready || saving}
                onChange={(event) => updateTemplate(template.id, {
                  intent: event.target.value === "lead" ? "lead" : "support",
                })}
              >
                <option value="support">Support</option>
                <option value="lead">Leads</option>
              </Select>
              <Input
                label="Subject"
                tooltip="The subject the customer sees. You can use the same placeholders as the body."
                value={template.subject}
                disabled={!ready || saving}
                onChange={(event) => updateTemplate(template.id, { subject: event.target.value })}
              />
              <label>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                  Body
                  <HintMark
                    label="About Body"
                    text="The message the customer receives. Placeholders are replaced with the sender's name, company, subject, and a one-line summary. The model does not rewrite this text."
                  />
                </span>
                <textarea
                  className="crm-textarea crm-textarea--activity"
                  value={template.body}
                  disabled={!ready || saving}
                  rows={5}
                  onChange={(event) => updateTemplate(template.id, { body: event.target.value })}
                />
              </label>
              <Cluster>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!ready || saving}
                  onClick={() => setValue({
                    ...value,
                    reply_templates: value.reply_templates.filter((item) => item.id !== template.id),
                  })}
                >
                  Remove template
                </Button>
              </Cluster>
            </Stack>
          ))}
          <Cluster>
            <Button
              type="button"
              variant="secondary"
              disabled={!ready || saving || value.reply_templates.length >= 8}
              onClick={() => setValue({ ...value, reply_templates: [...value.reply_templates, blankTemplate()] })}
            >
              Add template
            </Button>
          </Cluster>
          {error ? <p role="alert" className="settings-error">{error}</p> : null}
          {notice ? <p className="home-subtitle">{notice}</p> : null}
          <Button type="submit" disabled={saving || !ready}>
            {saving ? "Saving…" : "Save mailbox AI"}
          </Button>
        </Stack>
      </form>
    </Card>
  );
}
